import classnames from 'classnames'
import debounce from 'lodash.debounce'
import React from 'react'

import { Instrument } from '../../../types/shared'
import { Button, Dropdown, DropdownItem, Icon, Input, useDropdownState } from '../../components'
import { getSanitizedTicker } from '../../utils'
import fetchApi from '../../utils/fetch_api'

type FetchCallback = (data: any) => any
interface DataType {
  ticker: string
  company: string
  exchange: string
}

interface SearchProps {
  focus?: boolean
  ticker: string
  instrument: Instrument
  chartIndex: number
  onTickerSelected: (ticker: string) => void
}

interface SearchState {
  input: string
  highlight: string
  data: DataType[]
  focused: boolean
  selected: number
}

const handleFetch = async (input: string, callback: FetchCallback) => {
  try {
    const data = await fetchApi({
      location: '/api/suggestions.ashx',
      queryParameters: { input },
      throwOnStatusCodes: [404],
    })
    if (Array.isArray(data)) {
      callback(data)
    }
  } catch {
    // ignore error
  }
}

class Search extends React.Component<SearchProps, SearchState> {
  search = debounce((input: string, callback: FetchCallback) => {
    handleFetch(input, callback)
  }, 500)

  state = this.getBaseState()

  getBaseState() {
    return { input: '', highlight: '', data: [] as DataType[], focused: false, selected: -1 }
  }

  handleDebounceCancel = () => {
    this.search.cancel()
  }

  render() {
    const focus = !!this.props.focus
    return (
      <div className="search flex flex-none items-center">
        <SearchInput
          onChange={this._onChange}
          onFocus={this._onFocus}
          onBlur={this._onBlur}
          onArrowUp={this._onArrowUp}
          onArrowDown={this._onArrowDown}
          value={this.state.input}
          instrument={this.props.instrument}
          data={this.state.data}
          focus={focus}
          onTickerSelected={this._onTickerSelected}
          currentTicker={this.props.ticker}
          cancelDebounce={this.handleDebounceCancel}
          chartIndex={this.props.chartIndex}
        />
        <SuggestionsBox
          input={this.state.input}
          highlight={this.state.highlight}
          data={this.state.data}
          selected={this.state.selected}
          focused={this.state.focused}
          onTickerSelected={this._onTickerSelected}
          chartIndex={this.props.chartIndex}
        />
      </div>
    )
  }

  _onChange = (value: string) => {
    const callback = (data: DataType[]) => {
      this.setState({ data: data, highlight: value })
    }
    this.setState({ input: value, selected: -1 })
    if (value === '') {
      callback([])
      return
    }
    this.search(value, callback)
  }

  _onFocus = () => {
    this.setState({ focused: true })
  }

  _onBlur = () => {
    this.setState({ focused: false })
  }

  _onArrowUp = () => {
    if (this.state.selected > 0) {
      const selected = this.state.selected - 1
      this.setState({ selected: selected, input: this.state.data[selected].ticker })
    }
  }

  _onArrowDown = () => {
    if (this.state.selected < this.state.data.length - 1) {
      const selected = this.state.selected + 1
      this.setState({ selected: selected, input: this.state.data[selected].ticker })
    }
  }

  _onTickerSelected = (ticker: string) => {
    this.setState({
      ...this.getBaseState(),
      focused: this.state.focused,
    })
    this.props.onTickerSelected(ticker)
  }
}

interface SearchInputProps {
  data: DataType[]
  focus: boolean
  currentTicker: string
  value: string
  instrument: Instrument
  chartIndex: number
  cancelDebounce: () => void
  onArrowUp: () => void
  onArrowDown: () => void
  onChange: (value: string) => void
  onFocus: (event: React.FocusEvent<HTMLInputElement>) => void
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  onTickerSelected: (ticker: string) => void
}

function SearchInput(props: SearchInputProps) {
  const { cancelDebounce, value, onTickerSelected, onChange, onArrowUp, onArrowDown } = props
  const dropdownState = useDropdownState({ placement: 'bottom-end' })

  const onSubmit = React.useCallback(
    (ev: React.FormEvent<HTMLFormElement>) => {
      ev.preventDefault()
      cancelDebounce()
      if (value !== '') {
        const sanitizedValue = getSanitizedTicker(value)
        onTickerSelected(sanitizedValue)
      }
    },
    [cancelDebounce, onTickerSelected, value]
  )

  const onInputChange = React.useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      ev.preventDefault()
      onChange(ev.currentTarget.value.trim())
    },
    [onChange]
  )

  const onInputKeyDown = React.useCallback((ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'ArrowUp' || ev.key === 'ArrowDown') {
      ev.preventDefault()
    }
  }, [])

  const onInputKeyUp = React.useCallback(
    (ev: React.KeyboardEvent<HTMLInputElement>) => {
      switch (ev.key) {
        case 'ArrowUp':
          ev.preventDefault()
          onArrowUp()
          break
        case 'ArrowDown':
          ev.preventDefault()
          onArrowDown()
          break
      }
    },
    [onArrowUp, onArrowDown]
  )

  const detailLink = React.useMemo(() => {
    const ticker = props.currentTicker.replace('@', '')

    switch (props.instrument) {
      case Instrument.Stock:
        return `/quote.ashx?t=${ticker}`
      case Instrument.Futures:
        return `/futures_charts.ashx?t=${ticker}`
      case Instrument.Forex:
        return `/forex_charts.ashx?t=${ticker}`
      case Instrument.Crypto:
        return `/crypto_charts.ashx?t=${ticker}`
    }
  }, [props.currentTicker, props.instrument])

  return (
    <form onSubmit={onSubmit} className="w-32">
      <Input
        size="medium"
        inputClass="pr-7 placeholder:text-gray-900 focus:placeholder:text-gray-500 dark:placeholder:text-white dark:focus:placeholder:text-gray-400"
        data-testid={`chart-${props.chartIndex}-settings-search-input`}
        value={value}
        placeholder={props.currentTicker}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        onChange={onInputChange}
        onKeyDown={onInputKeyDown}
        onKeyUp={onInputKeyUp}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        rightContent={
          <Dropdown
            state={dropdownState}
            aria-label="More options"
            label={
              <Button
                type="button"
                size="none"
                className="h-full w-7 text-gray-400 hover:text-gray-500"
                theme="transparent"
                appearance="square"
                rightContent={<Icon width={20} name="more" />}
              />
            }
          >
            <DropdownItem as="a" target="_blank" href={detailLink}>
              {props.instrument === Instrument.Stock ? 'Open Stock Detail' : 'Open Detail'}
            </DropdownItem>
            {props.instrument === Instrument.Stock && (
              <DropdownItem as="a" target="_blank" href={`/save_to_portfolio.ashx?t=${props.currentTicker}`}>
                Save to Portfolio
              </DropdownItem>
            )}
            {props.instrument === Instrument.Stock && (
              <DropdownItem as="a" target="_blank" href={`/create_alert.ashx?t=${props.currentTicker}`}>
                Create Alert
              </DropdownItem>
            )}
          </Dropdown>
        }
      />
    </form>
  )
}

interface SuggestionsBoxProps {
  data: DataType[]
  onTickerSelected: (ticker: string) => void
  focused: boolean
  chartIndex: number
  selected: number
  highlight: string
  input: string
}

class SuggestionsBox extends React.Component<SuggestionsBoxProps> {
  render() {
    const data = this.props.data
    return (
      <table
        className="suggestions-box"
        style={{
          display: this.props.focused && (data.length > 0 || this.props.highlight.length > 2) ? 'table' : 'none',
        }}
        cellPadding="0"
        cellSpacing="0"
      >
        <tbody>
          {data.map((row, index) => (
            <tr
              onMouseDown={this._onClick.bind(this, row.ticker)}
              className={classnames({ active: index === this.props.selected })}
              key={row.ticker}
              data-testid={`chart-${this.props.chartIndex}-settings-suggestion-${row.ticker}`}
            >
              <td className="ticker" dangerouslySetInnerHTML={{ __html: this._formatTicker(row.ticker) }} />
              <td className="company" dangerouslySetInnerHTML={{ __html: this._formatCompany(row.company) }} />
              <td className="exchange">{row.exchange}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  _onClick(ticker: string, e: React.MouseEvent<HTMLTableRowElement>) {
    if (e.button === 0) {
      this.props.onTickerSelected(ticker)
    }
  }

  _formatTicker(ticker: string) {
    const hightlight = this.props.highlight.toUpperCase()
    return ticker.replace(hightlight, '<b>' + hightlight + '</b>')
  }

  _formatCompany(text: string) {
    const input = this.props.highlight
    const re = new RegExp(this._escapeRegExp(input), 'gi')
    return text.replace(re, function (match) {
      return '<b>' + match + '</b>'
    })
  }

  _escapeRegExp(str: string) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }
}

export default Search
